import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth";
import { useCart } from "../cart";
import { PageHead, useSeo } from "../ui";
import { CartPanel, LoginRequired } from "./Cart";
import { BankBox } from "./Checkout";

export const ORDER_STATUS = {
  PENDING: "입금 대기",
  PAID: "입금 확인 · 발송 준비",
  SHIPPED: "배송 중",
  DELIVERED: "배송 완료",
  CANCELLED: "주문 취소",
};

const TABS = [
  ["cart", "장바구니"],
  ["orders", "주문내역"],
  ["purchases", "구매내역"],
];

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function OrderCard({ order, onCancel }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const cancel = async () => {
    if (!window.confirm(`주문 #${order.order_no} 을(를) 취소할까요?`)) return;
    setBusy(true);
    try { await onCancel(order.order_no); } finally { setBusy(false); }
  };

  const first = order.items[0];
  const more = order.items.length - 1;
  const card = order.pay_method === "CARD";
  const pay = order.pay || {};

  return (
    <article className={`order-card order-card--${order.status.toLowerCase()}`}>
      {/* 한 주문은 두 줄 — 1) 주문번호 · 날짜 · 상태  2) 상품 · 금액 · [상세 보기] */}
      <div className="order-card__row">
        <div className="order-card__id">
          <span className="order-card__no"><span className="order-card__no-label">주문번호 </span>#{order.order_no}</span>
          <span className="order-card__date">{fmtDate(order.created_at)}</span>
        </div>
        <span className={`order-badge order-badge--${order.status.toLowerCase()}`}>
          {card && order.status === "PENDING" ? (pay.review ? "테스트 결제" : "결제 전") : card && order.status === "PAID" ? "결제 완료 · 발송 준비" : ORDER_STATUS[order.status] || order.status}
        </span>
      </div>

      <div className="order-card__row order-card__row--sub">
        <span className="order-card__title" title={first ? first.name : undefined}>
          {first ? first.name : "상품"}{more > 0 && ` 외 ${more}건`}
        </span>
        <div className="order-card__end">
          <strong className="order-card__total">{order.total.toLocaleString("ko-KR")}원</strong>
          <button type="button" className="order-card__toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {open ? "상세 닫기" : "상세 보기"}
            <svg className="order-card__chev" viewBox="0 0 12 12" width="12" height="12" aria-hidden="true" focusable="false">
              <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>

      {open && (
        <div className="order-card__detail">
          {card && order.status === "PENDING" && (
            <p className="order-card__hint">
              {pay.review
            ? "카드결제 심사용 테스트 결제 주문입니다. 실제로 청구되지 않았고 주문은 확정되지 않았습니다. 아래 [주문 취소]로 정리해 주세요."
            : "카드결제가 끝나지 않은 주문입니다. 아래 [주문 취소] 후 다시 주문해 주세요."}
            </p>
          )}
          {!card && order.status === "PENDING" && (
            <div className="order-card__pay">
              <p className="order-card__hint">
                아래 계좌로 <strong>{order.total.toLocaleString("ko-KR")}원</strong>을 입금자명{" "}
                <strong>{order.depositor}</strong>(으)로 보내 주세요. 확인 후 발송합니다.
              </p>
              <BankBox />
            </div>
          )}
          <ul className="order-card__items">
            {order.items.map((i) => (
              <li key={i.slug}>
                <Link to={`/products/${i.slug}`}>{i.name}</Link>
                <span className="order-card__code">{i.code}</span>
                <span>{i.price.toLocaleString("ko-KR")}원 × {i.qty}</span>
                <strong>{(i.price * i.qty).toLocaleString("ko-KR")}원</strong>
              </li>
            ))}
          </ul>
          <dl className="order-card__meta">
            <div><dt>상품금액</dt><dd>{order.subtotal.toLocaleString("ko-KR")}원</dd></div>
            <div><dt>배송비</dt><dd>{order.shipping ? `${order.shipping.toLocaleString("ko-KR")}원` : "무료"}</dd></div>
            <div><dt>받는 분</dt><dd>{order.receiver_name} · {order.phone}</dd></div>
            <div>
              <dt>배송지</dt>
              <dd>{order.zip && `(${order.zip}) `}{order.address} {order.address2}</dd>
            </div>
            {order.memo && <div><dt>배송 메모</dt><dd>{order.memo}</dd></div>}
            {order.tracking && <div><dt>송장</dt><dd>{order.tracking}</dd></div>}
            {card && (
              <div>
                <dt>결제</dt>
                <dd>
                  카드결제{pay.card ? ` · ${pay.card}` : ""}{pay.installment ? ` · ${pay.installment}개월 할부` : ""}{pay.test ? " (테스트)" : ""}
                  {pay.receipt_url && <> · <a href={pay.receipt_url} target="_blank" rel="noreferrer">영수증</a></>}
                  {pay.refund && " · 환불됨"}
                </dd>
              </div>
            )}
            {order.paid_at && <div><dt>{card ? "결제 완료" : "입금 확인"}</dt><dd>{fmtDate(order.paid_at)}</dd></div>}
            {order.shipped_at && <div><dt>발송</dt><dd>{fmtDate(order.shipped_at)}</dd></div>}
            {order.delivered_at && <div><dt>배송 완료</dt><dd>{fmtDate(order.delivered_at)}</dd></div>}
            {order.cancelled_at && <div><dt>취소</dt><dd>{fmtDate(order.cancelled_at)}</dd></div>}
          </dl>
          {order.status === "PENDING" && (
            <button type="button" className="btn btn--ghost order-card__cancel" onClick={cancel} disabled={busy}>
              {busy ? "취소 중…" : "주문 취소"}
            </button>
          )}
          {order.status !== "PENDING" && order.status !== "CANCELLED" && (
            <p className="order-card__hint">
              취소 · 교환 · 반품은 <Link to="/contact">문의</Link>로 주문번호와 함께 알려 주세요.
            </p>
          )}
        </div>
      )}
    </article>
  );
}

function OrderList({ orders, empty, onCancel }) {
  if (orders.length === 0) {
    return (
      <div className="shop-empty">
        <p>{empty}</p>
        <Link to="/products" className="btn btn--primary">판매제품 보러가기</Link>
      </div>
    );
  }
  return (
    <div className="order-list">
      {orders.map((o) => <OrderCard key={o.order_no} order={o} onCancel={onCancel} />)}
    </div>
  );
}

export default function My() {
  useSeo({ title: "MY | I.META", description: "장바구니 · 주문내역 · 구매내역", path: "/my", noindex: true });
  const { member, ready, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "cart";
  const [orders, setOrders] = useState({ status: "loading", items: [] });

  const load = useCallback(async () => {
    try {
      const res = await fetch("/api/orders/mine", { credentials: "same-origin" });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setOrders({ status: "ready", items: data.items || [] });
    } catch {
      setOrders({ status: "error", items: [] });
    }
  }, []);

  useEffect(() => { if (member) load(); }, [member, load]);

  const cancel = async (orderNo) => {
    const res = await fetch(`/api/orders/${orderNo}/cancel`, { method: "POST", credentials: "same-origin" });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      window.alert(typeof data.detail === "string" ? data.detail : "취소하지 못했습니다.");
      return;
    }
    setOrders((prev) => ({ ...prev, items: prev.items.map((o) => (o.order_no === orderNo ? data : o)) }));
  };

  if (!ready) return <section className="section shop"><p className="note-line">불러오는 중…</p></section>;
  if (!member) return <LoginRequired title="MY" />;

  const inProgress = orders.items.filter((o) => o.status !== "DELIVERED");
  const purchases = orders.items.filter((o) => o.status === "DELIVERED");
  const counts = { cart: count, orders: inProgress.filter((o) => o.status !== "CANCELLED").length, purchases: purchases.length };

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <section className="section shop my">
      <PageHead
        kicker="MY"
        title={`${member.name} 님`}
        desc={member.email}
        actions={
          <div className="my__links">
            <Link to="/account" className="btn btn--ghost">내 정보 수정</Link>
            <button type="button" className="btn btn--ghost" onClick={doLogout}>로그아웃</button>
          </div>
        }
      />

      <div className="shop-tabs" role="tablist" aria-label="MY 메뉴">
        {TABS.map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            className={`shop-tabs__tab ${tab === k ? "is-active" : ""}`}
            onClick={() => setParams(k === "cart" ? {} : { tab: k }, { replace: true })}
          >
            {label}
            {(k === "cart" || orders.status === "ready") && <span className="shop-tabs__count">{counts[k]}</span>}
          </button>
        ))}
      </div>

      {tab === "cart" && <CartPanel />}

      {tab !== "cart" && orders.status === "loading" && <p className="note-line">불러오는 중…</p>}
      {tab !== "cart" && orders.status === "error" && (
        <p className="note-line">
          주문 내역을 불러오지 못했습니다.{" "}
          <button type="button" className="cart__clear" onClick={load}>다시 시도</button>
        </p>
      )}

      {tab === "orders" && orders.status === "ready" && (
        <>
          <p className="my__note">입금 대기부터 배송 중까지의 주문과 취소한 주문입니다. 배송이 끝난 주문은 구매내역으로 옮겨집니다.</p>
          <OrderList orders={inProgress} empty="진행 중인 주문이 없습니다." onCancel={cancel} />
        </>
      )}
      {tab === "purchases" && orders.status === "ready" && (
        <>
          <p className="my__note">배송이 완료된 구매 내역입니다.</p>
          <OrderList orders={purchases} empty="아직 구매 완료된 상품이 없습니다." onCancel={cancel} />
        </>
      )}
    </section>
  );
}
